/**
 * EncoupleThinkingMark — the animated EnCouple logo as a chat thinking indicator.
 *
 * STATUS: final "living drift" design locked 2026-07-24. Its one consumer is
 * `ThinkingIndicator.js:38`, which mounts it as the chat waiting indicator.
 * Interactive tuning demo (same engine + live look sliders):
 * https://claude.ai/code/artifact/4100a30e-dba2-483b-880d-2bbb917fd1b6
 *
 * The loop: one continuous, never-stopping elastic spin. Angular velocity is a
 * raised-cosine oscillation between a slow floor and a fast peak, phase-warped
 * to linger through the slow trough; as the spin winds up, "centrifugal force"
 * morphs the three petals into three circles orbiting a shared ring, and as it
 * eases down they relax back into petals. The shape trails the speed (contrast
 * stretch + time lag) so the morph reads as the acceleration doing the pulling.
 * On top of that rides a quiet "life layer": a ±1.3% whole-mark scale breath
 * and a tiny per-petal radial drift, each on its own period chosen to be
 * incommensurate with the 3s primary cycle and with the others — so the
 * composite state never visibly repeats and the mark reads as alive rather
 * than mechanical.
 *
 * How it works:
 * - Petal outlines and true circles are sampled at 160 arc-length-matched
 *   points (winding-normalized, rotation-offset-aligned), so the morph flows
 *   shapes into each other rather than crossfading.
 * - Rotation is integrated incrementally from the velocity curve (the warped
 *   throttle has no elementary closed-form integral), so it never resets or
 *   jumps; the morph blend is the same throttle, contrast-stretched and lagged.
 * - The central axis is the mean of the three petal centroids (the mark is not
 *   centered in its viewBox); all circles orbit that axis at one shared radius.
 * - At rest the petals sit `restSep` further apart than in the raw logo
 *   (edge-to-edge gap measured at init, offset baked into the morph geometry).
 * - All instances share one clock + one computed frame per tick, so any number
 *   of indicators stay in sync for the price of one.
 *
 * Petal path data is duplicated from assets/icons/sprite.svg#logo-encouple-mark
 * because the engine must measure real path geometry (getPointAtLength/getBBox)
 * and the sprite symbol is not reliably reachable/measurable from here. If the
 * logo mark ever changes, update PETALS below to match the sprite.
 *
 * Usage:
 *   import { EncoupleThinkingMark } from './components/EncoupleThinkingMark.js';
 *   const mark = new EncoupleThinkingMark(hostElement, { size: 26 });
 *   // ... response arrives ...
 *   mark.destroy();
 *
 * Notes for integration:
 * - The SVG renders with overflow:visible so the separated petals / orbiting
 *   circles can breathe slightly past the box edge instead of clipping. Give
 *   it a couple of px of elbow room in tight rows.
 * - Honors prefers-reduced-motion: renders the static (separated) mark.
 * - Decorative by default (aria-hidden); pass {label} to expose as role="img".
 *
 * @module components/EncoupleThinkingMark
 */

const SVG_NS = 'http://www.w3.org/2000/svg';
const VIEWBOX = '0 0 51.622684 49.720707';
const GROUP_TRANSFORM = 'translate(-71.744449,-69.485796)';
const SAMPLES = 160;

/*
 * Hand-tuned motion constants (the animation's feel — not part of the look
 * config on purpose; tuning history lives in the artifact demos). Verified
 * numerically at 60fps: no per-frame blend jump above 0.044, velocity
 * continuous everywhere, all layers C1-smooth at their loop seams.
 */
const OMEGA_MIN = 0.055; // slow floor, deg/ms (55 deg/s) — never zero, never stops
const OMEGA_MAX = 0.48;  // fast peak, deg/ms (480 deg/s) — grazed only for an instant
const PERIOD_MS = 3000;  // one full wind-up + wind-down at speed 1.0
const SLOW_BIAS = 0.21;  // phase warp: lingers through the slow (petal) trough so the
                         // petal phase lasts ~30% longer than the circle burst (< 1)
const SHAPE_C = 0.11;    // shape contrast: throttle in [C, 1-C] maps to the full
                         // petal..circle morph, so shapes resolve cleanly at extremes
const SHAPE_LAG = 0.12;  // shape trails speed by this fraction of the period — petals
                         // spin up BEFORE morphing, circles hold into the slowdown

/*
 * Life layer: incommensurate secondary rhythms. Periods deliberately share no
 * small common multiple with the 3s primary cycle or with each other, so the
 * composite state never visibly loops. Amplitudes stay well under the primary
 * motion (the ~20% ambient cap) so the layer reads as micro-life, not noise.
 */
const BREATH2_MS = 5300;   // whole-mark scale-breath period
const BREATH2_AMP = 0.013; // ±1.3% scale — far below the ±5% attention threshold
const WOBBLE = [           // per-petal radial drift (viewBox units, ~1% of mark)
  { T: 4300, A: 0.45, ph: 0.0 },
  { T: 5100, A: 0.50, ph: 2.1 },
  { T: 6100, A: 0.40, ph: 4.2 }
];

const TWO_PI = Math.PI * 2;

/**
 * The locked look configuration — final "living drift" values chosen 2026-07-24
 * on the tuning demo. Treat as the single source of truth for the look; change
 * here (not per-instance) so every surface stays identical.
 *
 * @type {Readonly<{speed: number, restSep: number, circleR: number,
 *   circlePull: number, drift: number}>}
 */
export const ENCOUPLE_MARK_CONFIG = Object.freeze({
  speed: 1.15,     // tempo multiplier on the whole oscillation
  restSep: 1.3,    // resting separation: +130% edge-to-edge gap between petals
  circleR: 7.5,    // circle radius (viewBox units)
  circlePull: 1.0, // circle orbit radius as a fraction of mean centroid distance
  drift: 1.0       // life-layer amplitude multiplier (scale breath + petal drift)
});

/**
 * One full wind-up + wind-down in wall-clock ms — `PERIOD_MS` scaled by the
 * configured speed. The cycle starts (and so ends) at the slow trough, so a
 * surface that stops the mark on an exact multiple of this lands on the resting
 * petal pose instead of freezing mid-morph. Exported so consumers that sync to
 * the mark (the memory-update card) can't drift if `speed` is ever retuned.
 * @type {number}
 */
export const ENCOUPLE_MARK_CYCLE_MS = Math.round(PERIOD_MS / ENCOUPLE_MARK_CONFIG.speed);

/** Petal fill colors + outlines from sprite.svg#logo-encouple-mark. */
const PETALS = [
  {
    color: '#43a3bc',
    d: 'm 95.948584,71.455853 c 1.463573,-0.818574 3.528125,-0.932545 5.165266,-1.137196 6.59257,-0.818565 14.21613,1.530931 18.66386,6.628846 4.45031,5.100509 5.09532,13.633294 0.0414,18.586152 -6.318,6.193645 -16.23665,4.116153 -19.37622,-4.157605 -1.598275,-4.209407 -0.29013,-8.719294 -1.108692,-13.047861 -0.297895,-1.574969 -0.935139,-3.170655 -1.826232,-4.502124 -0.404103,-0.60097 -1.559421,-1.647497 -1.559421,-2.370212 z'
  },
  {
    color: '#c84e0c',
    d: 'm 79.850883,102.61576 c -1.440693,-0.8582 -2.571672,-2.58917 -3.567474,-3.904649 -4.005177,-5.30005 -5.782241,-13.07699 -3.591176,-19.47779 2.192016,-6.40434 9.259133,-11.229325 16.075389,-9.328949 8.522858,2.374715 11.683008,12.003274 6.08751,18.859098 -2.846315,3.488855 -7.406075,4.610906 -10.745439,7.484095 -1.215012,1.045459 -2.27829,2.39518 -2.98584,3.832625 -0.318406,0.65045 -0.647054,2.17424 -1.272941,2.5356 z'
  },
  {
    color: '#f7c708',
    d: 'm 115.12114,101.05704 c -0.0229,1.67678 -0.95641,3.52173 -1.59778,5.04185 -2.5874,6.11861 -8.43389,11.54607 -15.072676,12.84895 -6.642337,1.30383 -14.354453,-2.40398 -16.116812,-9.25723 -2.204851,-8.56836 4.553641,-16.119413 13.288708,-14.701482 4.44459,0.720553 7.69621,4.108398 11.85414,5.563782 1.51289,0.52949 3.21342,0.77546 4.81207,0.6695 0.72251,-0.0495 2.20647,-0.52676 2.83236,-0.1654 z'
  }
];

/* ---------- motion curves ---------- */

/** Quintic smoothstep — zero velocity AND acceleration at both ends. */
function smootherstep(t) {
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return t * t * t * (t * (t * 6 - 15) + 10);
}

/** @param {number} x @returns {number} x clamped to [0, 1] */
function clamp01(x) {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

/**
 * Throttle s(t) in [0,1]: raised-cosine of a phase warped to linger through
 * the slow trough (SLOW_BIAS). Continuous, briefly at each extreme, always
 * moving. 0 = slow floor, 1 = fast peak.
 * @param {number} t animation ms
 * @returns {number} throttle 0..1
 */
function throttle(t) {
  let phi = (t / PERIOD_MS) % 1;
  if (phi < 0) phi += 1;
  const g = phi - SLOW_BIAS * Math.sin(TWO_PI * phi) / TWO_PI;
  return (1 - Math.cos(TWO_PI * g)) / 2;
}

/**
 * Instantaneous angular velocity (deg/ms). Integrated incrementally in the
 * clock (the warped throttle has no elementary closed-form integral).
 * @param {number} t animation ms
 * @returns {number} deg/ms
 */
function omega(t) {
  return OMEGA_MIN + (OMEGA_MAX - OMEGA_MIN) * throttle(t);
}

/**
 * Shape blend 0=petal .. 1=circle. Decoupled from speed two ways:
 * contrast-stretched (SHAPE_C) so shapes resolve cleanly at the extremes, and
 * time-lagged (SHAPE_LAG) so the morph trails the speed — petals spin up
 * before stretching into circles, and circles hold into the slowdown.
 * @param {number} t animation ms
 * @returns {number} blend 0..1
 */
function shapeBlend(t) {
  const lagged = throttle(t - SHAPE_LAG * PERIOD_MS);
  return smootherstep(clamp01((lagged - SHAPE_C) / (1 - 2 * SHAPE_C)));
}

/* ---------- geometry ---------- */

/** Sample n points along a live path element, uniform by arc length. */
function samplePath(pathEl, n) {
  const L = pathEl.getTotalLength();
  const pts = new Array(n);
  for (let i = 0; i < n; i++) {
    const p = pathEl.getPointAtLength((L * i) / n);
    pts[i] = [p.x, p.y];
  }
  return pts;
}

/** Signed polygon area — used to compare winding directions. */
function signedArea(pts) {
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    const q = pts[(i + 1) % pts.length];
    a += p[0] * q[1] - q[0] * p[1];
  }
  return a / 2;
}

/** Match dst's winding + start index to src so morph point-travel is minimal. */
function alignRings(src, dst) {
  let d = dst;
  if (signedArea(src) * signedArea(d) < 0) d = d.slice().reverse();
  let best = 0;
  let bestDist = Infinity;
  for (let off = 0; off < d.length; off++) {
    let sum = 0;
    for (let i = 0; i < src.length; i += 4) {
      const dx = src[i][0] - d[(i + off) % d.length][0];
      const dy = src[i][1] - d[(i + off) % d.length][1];
      sum += dx * dx + dy * dy;
      if (sum >= bestDist) break;
    }
    if (sum < bestDist) { bestDist = sum; best = off; }
  }
  const out = new Array(d.length);
  for (let j = 0; j < d.length; j++) out[j] = d[(j + best) % d.length];
  return out;
}

/** Closed polyline path string from ring points. */
function ringToPath(pts) {
  let s = 'M' + pts[0][0].toFixed(2) + ' ' + pts[0][1].toFixed(2);
  for (let i = 1; i < pts.length; i++) {
    s += 'L' + pts[i][0].toFixed(2) + ' ' + pts[i][1].toFixed(2);
  }
  return s + 'Z';
}

/** Interpolate ring a→b at t into preallocated `out`. */
function lerpRing(a, b, t, out) {
  for (let i = 0; i < a.length; i++) {
    out[i][0] = a[i][0] + (b[i][0] - a[i][0]) * t;
    out[i][1] = a[i][1] + (b[i][1] - a[i][1]) * t;
  }
  return out;
}

/** Min edge-to-edge distance between two sampled outlines. */
function ringGap(a, b) {
  let m = Infinity;
  for (let i = 0; i < a.length; i++) {
    for (let j = 0; j < b.length; j++) {
      const dx = a[i][0] - b[j][0];
      const dy = a[i][1] - b[j][1];
      const d2 = dx * dx + dy * dy;
      if (d2 < m) m = d2;
    }
  }
  return Math.sqrt(m);
}

/**
 * The petal d strings open with a relative moveto ("m x,y c ..."), so
 * translating a whole petal only requires shifting that first coordinate pair.
 */
function shiftPathD(d, dx, dy) {
  return d.replace(/^m\s+(-?[\d.]+),(-?[\d.]+)/, (_, x, y) =>
    'm ' + (parseFloat(x) + dx).toFixed(6) + ',' + (parseFloat(y) + dy).toFixed(6));
}

/* ---------- shared engine state (one per page) ---------- */

const CONFIG = ENCOUPLE_MARK_CONFIG;
let geo = null;              // [{color, petalD, petalRing, circleD, circleRing, dir, centroid, scratch}]
const AXIS = { x: 0, y: 0 }; // spin axis = mean of petal centroids
const instances = new Set();
let rafId = null;
let elapsed = 0;   // phase clock (drives throttle, shape, and life layer)
let spinDeg = 0;   // accumulated rotation, integrated from omega() over time
let lastTs = null;

/**
 * Measure the logo geometry once: sample petal outlines, derive the axis,
 * bake in the resting separation, and build the aligned circle targets.
 * Requires a live DOM (getTotalLength/getBBox) — called lazily on first use.
 */
function buildGeometry() {
  const measureSvg = document.createElementNS(SVG_NS, 'svg');
  measureSvg.setAttribute('viewBox', VIEWBOX);
  measureSvg.setAttribute('aria-hidden', 'true');
  measureSvg.style.cssText = 'position:absolute;left:-9999px;top:0;width:200px;height:200px;visibility:hidden';
  const measureGroup = document.createElementNS(SVG_NS, 'g');
  measureGroup.setAttribute('transform', GROUP_TRANSFORM);
  measureSvg.appendChild(measureGroup);
  const measurePath = document.createElementNS(SVG_NS, 'path');
  measureGroup.appendChild(measurePath);
  document.body.appendChild(measureSvg);

  try {
    const g = PETALS.map((petal) => {
      measurePath.setAttribute('d', petal.d);
      const ring = samplePath(measurePath, SAMPLES);
      const bb = measurePath.getBBox();
      return {
        color: petal.color,
        origD: petal.d,
        origRing: ring,
        origCentroid: [bb.x + bb.width / 2, bb.y + bb.height / 2],
        petalD: '',
        petalRing: null,
        centroid: null,
        dir: [0, 0],
        circleRing: null,
        circleD: '',
        scratch: ring.map((p) => [p[0], p[1]])
      };
    });

    AXIS.x = (g[0].origCentroid[0] + g[1].origCentroid[0] + g[2].origCentroid[0]) / 3;
    AXIS.y = (g[0].origCentroid[1] + g[1].origCentroid[1] + g[2].origCentroid[1]) / 3;
    for (const p of g) {
      const dx = p.origCentroid[0] - AXIS.x;
      const dy = p.origCentroid[1] - AXIS.y;
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      p.dir = [dx / len, dy / len];
    }

    // Resting separation: grow the measured edge-to-edge gap by restSep.
    // Petals sit ~120° apart, so mutual separation grows ~sqrt(3) per unit
    // of radial travel. Baked into rings/paths so the morph starts from the
    // separated positions.
    const baseGap = (ringGap(g[0].origRing, g[1].origRing) +
                     ringGap(g[0].origRing, g[2].origRing) +
                     ringGap(g[1].origRing, g[2].origRing)) / 3;
    const offset = (baseGap * CONFIG.restSep) / Math.sqrt(3);
    let meanDist = 0;
    for (const p of g) {
      const ox = p.dir[0] * offset;
      const oy = p.dir[1] * offset;
      p.petalRing = p.origRing.map((pt) => [pt[0] + ox, pt[1] + oy]);
      p.petalD = offset === 0 ? p.origD : shiftPathD(p.origD, ox, oy);
      p.centroid = [p.origCentroid[0] + ox, p.origCentroid[1] + oy];
      const dx = p.centroid[0] - AXIS.x;
      const dy = p.centroid[1] - AXIS.y;
      meanDist += Math.sqrt(dx * dx + dy * dy) / 3;
    }

    // Circle targets: every circle at the SAME radius from AXIS (along its
    // petal's own direction) so the three trace one shared, balanced orbit.
    const orbit = meanDist * CONFIG.circlePull;
    const r = CONFIG.circleR;
    for (const p of g) {
      const cx = AXIS.x + p.dir[0] * orbit;
      const cy = AXIS.y + p.dir[1] * orbit;
      measurePath.setAttribute('d',
        'M ' + (cx + r) + ' ' + cy +
        ' A ' + r + ' ' + r + ' 0 1 1 ' + (cx - r) + ' ' + cy +
        ' A ' + r + ' ' + r + ' 0 1 1 ' + (cx + r) + ' ' + cy + ' Z');
      p.circleRing = alignRings(p.petalRing, samplePath(measurePath, SAMPLES));
      p.circleD = ringToPath(p.circleRing);
    }
    return g;
  } finally {
    measureSvg.remove();
  }
}

/**
 * Compute the shared frame for phase-time t and apply it to every instance.
 * @param {number} t elapsed animation ms
 */
function render(t) {
  const w = shapeBlend(t);
  const frame = { dKind: 'petal', ds: null, deg: spinDeg % 360, scale: 1, offs: null };

  // Life layer: whole-mark scale breath + per-petal radial drift, each on its
  // own incommensurate period so the composite never visibly repeats.
  frame.scale = 1 + BREATH2_AMP * Math.sin(TWO_PI * t / BREATH2_MS) * CONFIG.drift;
  frame.offs = geo.map((g, i) => {
    const r = WOBBLE[i].A * Math.sin(TWO_PI * t / WOBBLE[i].T + WOBBLE[i].ph) * CONFIG.drift;
    return 'translate(' + (g.dir[0] * r).toFixed(3) + ' ' + (g.dir[1] * r).toFixed(3) + ')';
  });

  if (w >= 0.9995) {
    frame.dKind = 'circle';
  } else if (w > 0.0005) {
    frame.dKind = 'morph';
    frame.ds = geo.map((g) => ringToPath(lerpRing(g.petalRing, g.circleRing, w, g.scratch)));
  }

  for (const inst of instances) inst.apply(frame);
}

function tick(ts) {
  if (lastTs !== null) {
    const dScaled = Math.min(ts - lastTs, 100) * CONFIG.speed;
    spinDeg += omega(elapsed) * dScaled; // integrate rotation at current speed
    elapsed += dScaled;
    render(elapsed);
  }
  lastTs = ts;
  rafId = requestAnimationFrame(tick);
}

function startClock() {
  if (rafId === null) {
    lastTs = null;
    rafId = requestAnimationFrame(tick);
  }
}

function stopClockIfIdle() {
  if (instances.size === 0 && rafId !== null) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
}

/**
 * One animated logo mark. Create as many as needed — all share a single
 * clock/frame and stay in sync. Sizing follows the host element unless a
 * pixel `size` is given.
 */
export class EncoupleThinkingMark {
  /**
   * @param {HTMLElement} container host element the SVG is appended to
   * @param {Object} [opts]
   * @param {number} [opts.size] square size in px; omit to fill the container
   * @param {string} [opts.label] accessible name; omitted = decorative (aria-hidden)
   */
  constructor(container, opts = {}) {
    if (!geo) geo = buildGeometry();

    this.svg = document.createElementNS(SVG_NS, 'svg');
    this.svg.setAttribute('viewBox', VIEWBOX);
    // overflow:visible lets the separated petals / orbit breathe past the box
    this.svg.style.overflow = 'visible';
    this.svg.style.display = 'block';
    if (opts.size) {
      this.svg.style.width = opts.size + 'px';
      this.svg.style.height = opts.size + 'px';
    } else {
      this.svg.style.width = '100%';
      this.svg.style.height = '100%';
    }
    if (opts.label) {
      this.svg.setAttribute('role', 'img');
      this.svg.setAttribute('aria-label', opts.label);
    } else {
      this.svg.setAttribute('aria-hidden', 'true');
    }

    const outer = document.createElementNS(SVG_NS, 'g');
    outer.setAttribute('transform', GROUP_TRANSFORM);
    this.spinG = document.createElementNS(SVG_NS, 'g');
    outer.appendChild(this.spinG);
    this.svg.appendChild(outer);

    this.paths = geo.map((p) => {
      const el = document.createElementNS(SVG_NS, 'path');
      el.setAttribute('d', p.petalD);
      el.setAttribute('fill', p.color);
      this.spinG.appendChild(el);
      return el;
    });
    this.dKind = 'petal'; // petal | circle | morph
    this.lastOffs = [null, null, null];
    this.lastXf = null;

    container.appendChild(this.svg);

    // Reduced motion: leave the static (separated) resting mark.
    this.animated = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.animated) {
      instances.add(this);
      startClock();
    }
  }

  /**
   * Apply a computed frame. Internal — called by the shared clock.
   * @param {{dKind: string, ds: ?string[], deg: number, scale: number,
   *   offs: string[]}} frame
   */
  apply(frame) {
    if (frame.dKind === 'morph') {
      for (let i = 0; i < 3; i++) this.paths[i].setAttribute('d', frame.ds[i]);
      this.dKind = 'morph';
    } else if (frame.dKind !== this.dKind) {
      for (let i = 0; i < 3; i++) {
        this.paths[i].setAttribute('d', frame.dKind === 'petal' ? geo[i].petalD : geo[i].circleD);
      }
      this.dKind = frame.dKind;
    }
    for (let i = 0; i < 3; i++) {
      if (frame.offs[i] !== this.lastOffs[i]) {
        this.paths[i].setAttribute('transform', frame.offs[i]);
        this.lastOffs[i] = frame.offs[i];
      }
    }
    const xf = 'rotate(' + frame.deg.toFixed(3) + ' ' + AXIS.x + ' ' + AXIS.y + ')' +
      ' translate(' + AXIS.x + ' ' + AXIS.y + ') scale(' + frame.scale.toFixed(5) + ')' +
      ' translate(' + (-AXIS.x) + ' ' + (-AXIS.y) + ')';
    if (xf !== this.lastXf) {
      this.spinG.setAttribute('transform', xf);
      this.lastXf = xf;
    }
  }

  /** Remove the SVG and release the shared clock if this was the last instance. */
  destroy() {
    instances.delete(this);
    this.svg.remove();
    stopClockIfIdle();
  }
}
